import React from 'react';
import { Box, Typography, Paper, Link } from '@mui/material';
import SectionTitle from '../components/SectionTitle';
import { useDataLastModified } from '../hooks/useDataLastModified';
import { t } from '../i18n';

const EMAIL = 'issues@generative-art-inc.com';

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{ p: 3, mb: 3, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
  >
    <Typography variant="h6" gutterBottom>{title}</Typography>
    {children}
  </Paper>
);

const AboutPage = () => {
  const updated = useDataLastModified();
  // Split around {email} so the word can be a mailto link in either language.
  const apiUsage = t(
    'The public API is limited to 5 requests/second. Need more? A $5/month plan covers most higher-volume usage — {email} the contact above to subscribe. Very large volumes by separate arrangement.',
    '公開APIは秒間5リクエストに制限されています。月額5ドルのプランでほとんどの大量利用をカバーできます。ご加入をご希望の場合は、上記の連絡先まで{email}でお申し込みください。非常に大量のご利用は個別対応となります。'
  ).split('{email}');

  return (
    <Box>
      <SectionTitle emoji="ℹ️">
        {t('Contact, Responsibility & Accuracy', 'お問い合わせ・免責・正確性')}
      </SectionTitle>

      <Section title={t('Contact', 'お問い合わせ')}>
        <Typography variant="body1" paragraph>
          {t(
            'For questions, corrections, or to report an issue, email:',
            'ご質問・修正のご依頼・不具合のご報告は、メールにてお願いします：'
          )}
        </Typography>
        <Link href={`mailto:${EMAIL}`} color="secondary" sx={{ fontFamily: 'monospace' }}>
          {EMAIL}
        </Link>
      </Section>

      <Section title={t('API usage & limits', 'API の利用と制限')}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {apiUsage[0]}
          <Link href={`mailto:${EMAIL}`} color="secondary">
            {t('email', 'メール')}
          </Link>
          {apiUsage[1]}
        </Typography>
      </Section>

      <Section title={t('Responsibility', '免責事項')}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t(
            'This service is provided as-is, on a best-effort basis, with no warranty of any kind. English / romaji renderings are machine-generated and may contain errors — verify important addresses (especially company names) before relying on them. This site is not affiliated with, nor endorsed by, Japan Post.',
            '本サービスは現状のまま、ベストエフォートで提供され、いかなる保証も行いません。英語・ローマ字表記は自動変換のため誤りを含む場合があります。重要な住所（特に会社名）は、ご利用前に必ずご確認ください。本サイトは日本郵便株式会社とは関係なく、同社の承認を受けたものでもありません。'
          )}
        </Typography>
      </Section>

      <Section title={t('Accuracy & coverage', '正確性とカバー範囲')}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t(
            'Built from Japan Post’s official postal-code data, this lookup covers every normal address and every 大口事業所 (large-volume business) address — full coverage of the dataset. Kanji values come straight from Japan Post and are authoritative; romaji / English is generated and may differ from a place’s or company’s own preferred spelling.',
            '日本郵便の公式郵便番号データをもとに構築しており、すべての通常の住所と、すべての大口事業所（企業）の住所を収録しています（データ全体を網羅）。漢字の値は日本郵便のデータそのままで正確です。ローマ字・英語表記は自動生成のため、地名や会社名が独自に用いる表記と異なる場合があります。'
          )}
        </Typography>
        {updated && (
          <Typography variant="body2" color="text.secondary">
            {t('Data last updated: ', 'データ最終更新日: ')}
            {updated}
          </Typography>
        )}
      </Section>

      <Section title={t('Data source & rights', 'データの出典・権利')}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('Source: Japan Post', 'データ提供: 日本郵便')} (
          <Link
            href="https://www.post.japanpost.jp/zipcode/download.html"
            target="_blank"
            rel="noopener"
            color="secondary"
          >
            japanpost.jp
          </Link>
          ).
        </Typography>
        <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }}>
          「郵便番号データに限っては日本郵便株式会社は著作権を主張しません。自由に配布していただいて結構です。」
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(
            '(Japan Post does not assert copyright on the postal-code data; you are free to redistribute it.)',
            '（郵便番号データについて、日本郵便株式会社は著作権を主張していません。自由に再配布できます。）'
          )}
        </Typography>
      </Section>
    </Box>
  );
};

export default AboutPage;
